import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Plus, FileText, Download, Users, Pencil, GripVertical, Sparkles, Mic, MessageCircle, PenLine, File, Trash2, Clock, ChevronDown, ChevronRight } from 'lucide-react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import AppShell from '@/components/AppShell';
import CreateChapterModal from '@/components/CreateChapterModal';
import ChapterCard from '@/components/ChapterCard';
import ExportBookModal from '@/components/ExportBookModal';
import CollaboratorsModal from '@/components/CollaboratorsModal';
import EditBookModal from '@/components/EditBookModal';
import EditChapterModal from '@/components/EditChapterModal';
import SkeletonCustomizerModal from '@/components/SkeletonCustomizerModal';
import InterviewMode from '@/components/InterviewMode';
import { toast } from 'sonner';
import { StoryEmptyState, StoryProgress, StorySectionHeader, StorySurface } from '@/components/story/StoryPrimitives';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INTERVIEW_SECTION_ARCS = {
  memoir: [
    { key: 'early_life', label: 'Early Life' },
    { key: 'growing_up', label: 'Growing Up' },
    { key: 'finding_path', label: 'Finding Your Path' },
    { key: 'love_family', label: 'Love & Family' },
    { key: 'wisdom', label: 'Wisdom & Legacy' },
  ],
  lifestory: [
    { key: 'early_life', label: 'Early Life' },
    { key: 'growing_up', label: 'Growing Up' },
    { key: 'finding_path', label: 'Finding Your Path' },
    { key: 'love_family', label: 'Love & Family' },
    { key: 'wisdom', label: 'Wisdom & Legacy' },
  ],
  fiction: [
    { key: 'characters', label: 'Characters' },
    { key: 'world', label: 'The World' },
    { key: 'plot', label: 'The Plot' },
  ],
  shortstory: [
    { key: 'setup', label: 'Setting the Stage' },
    { key: 'conflict', label: 'The Conflict' },
    { key: 'resolution', label: 'The Resolution' },
  ],
  business: [
    { key: 'reader_problem', label: "The Reader's Problem" },
    { key: 'your_approach', label: 'Your Approach' },
    { key: 'credibility', label: 'Your Credibility' },
    { key: 'case_studies', label: 'Case Studies' },
    { key: 'transformation', label: 'The Transformation' },
  ],
  childrens: [
    { key: 'hero', label: 'The Hero' },
    { key: 'setting_world', label: 'The World' },
    { key: 'problem_adventure', label: 'The Adventure' },
    { key: 'lesson_heart', label: 'The Heart of the Story' },
    { key: 'ending_joy', label: 'The Ending' },
  ],
  freestyle: [
    { key: 'beginning', label: 'Beginning' },
    { key: 'development', label: 'Development' },
    { key: 'climax', label: 'Climax' },
    { key: 'resolution', label: 'Resolution' },
  ],
};

const getInterviewSections = (genre) => {
  const normalized = (genre || 'memoir').toLowerCase();
  return INTERVIEW_SECTION_ARCS[normalized] || INTERVIEW_SECTION_ARCS.memoir;
};

// Sortable wrapper for ChapterCard
function SortableChapterCard({ chapter, onDelete, onEdit, onClick }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: chapter.chapter_id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="relative group">
      {/* Drag Handle */}
      <div
        {...attributes}
        {...listeners}
        className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-10 opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing p-2 rounded-lg hover:bg-[#E8E0D4]/60 dark:hover:bg-[#2A2520]/50"
        title="Drag to reorder"
      >
        <GripVertical className="w-5 h-5 text-[#7C766E] dark:text-[#5C5650]" />
      </div>
      <ChapterCard
        chapter={chapter}
        onDelete={onDelete}
        onEdit={onEdit}
        onClick={onClick}
      />
    </div>
  );
}

function BookDetailPage() {
  const { bookId } = useParams();
  const navigate = useNavigate();
  const [book, setBook] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [exports, setExports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showCollaboratorsModal, setShowCollaboratorsModal] = useState(false);
  const [showEditBookModal, setShowEditBookModal] = useState(false);
  const [showSkeletonModal, setShowSkeletonModal] = useState(false);
  const [showChapterPlan, setShowChapterPlan] = useState(false);
  const [showInterviewMode, setShowInterviewMode] = useState(false);
  const [editingChapter, setEditingChapter] = useState(null);
  const [interviewProgress, setInterviewProgress] = useState(null);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const hasAutoOpenedInterviewRef = useRef(false);

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const loadBookData = useCallback(async () => {
    try {
      const [bookRes, chaptersRes, exportsRes] = await Promise.all([
        axios.get(`${API}/books/${bookId}`),
        axios.get(`${API}/books/${bookId}/chapters`),
        axios.get(`${API}/books/${bookId}/exports`).catch(() => ({ data: [] }))
      ]);
      setBook(bookRes.data);
      setChapters(chaptersRes.data);
      setExports(exportsRes.data || []);
      
      // Load interview progress if interview mode book
      if (bookRes.data.is_interview_mode) {
        try {
          const progressRes = await axios.get(`${API}/books/${bookId}/interview-progress`);
          setInterviewProgress(progressRes.data);
        } catch (e) {
          // No interview progress found - this is normal for new books
        }
      }
    } catch (error) {
      console.error('Failed to load book:', error);
    } finally {
      setLoading(false);
    }
  }, [bookId]);

  useEffect(() => {
    loadBookData();
  }, [bookId, loadBookData]);

  // Auto-trigger Interview Mode once per page visit for interview-mode books with no chapters.
  // The ref ensures we never auto-open again even if data re-loads after the user closes it.
  useEffect(() => {
    if (book && book.is_interview_mode && chapters.length === 0 && !hasAutoOpenedInterviewRef.current) {
      hasAutoOpenedInterviewRef.current = true;
      setShowInterviewMode(true);
    }
  }, [book, chapters]);

  // Download an export
  const handleDownloadExport = async (exportItem) => {
    try {
      const response = await axios.get(`${API}/exports/${exportItem.export_id}/download`);
      
      if (response.data.file_content) {
        // Decode base64 and download
        const byteCharacters = atob(response.data.file_content);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        
        let mimeType = 'application/octet-stream';
        if (exportItem.format === 'pdf') mimeType = 'application/pdf';
        else if (exportItem.format === 'word') mimeType = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
        else if (exportItem.format === 'epub') mimeType = 'application/epub+zip';
        
        const blob = new Blob([byteArray], { type: mimeType });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = exportItem.file_name;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast.success('Download started!');
      }
    } catch (error) {
      console.error('Failed to download:', error);
      toast.error('Failed to download export');
    }
  };

  // Delete an export
  const handleDeleteExport = async (exportId) => {
    try {
      await axios.delete(`${API}/exports/${exportId}`);
      setExports(exports.filter(e => e.export_id !== exportId));
      toast.success('Export deleted');
    } catch (error) {
      console.error('Failed to delete export:', error);
      toast.error('Failed to delete export');
    }
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const handleCreateChapter = async (chapterData) => {
    try {
      const response = await axios.post(
        `${API}/books/${bookId}/chapters`,
        chapterData,
        
      );
      setChapters([...chapters, response.data].sort((a, b) => a.chapter_number - b.chapter_number));
      setShowCreateModal(false);
    } catch (error) {
      console.error('Failed to create chapter:', error);
    }
  };

  const handleDeleteChapter = async (chapterId) => {
    try {
      await axios.delete(`${API}/chapters/${chapterId}`);
      setChapters(chapters.filter(c => c.chapter_id !== chapterId));
    } catch (error) {
      console.error('Failed to delete chapter:', error);
    }
  };

  const handleBookUpdate = (updatedBook) => {
    setBook(updatedBook);
  };

  const handleTitleSave = async () => {
    const trimmed = titleDraft.trim();
    setEditingTitle(false);
    if (!trimmed || trimmed === book.title) return;
    try {
      const response = await axios.put(`${API}/books/${bookId}`, {
        title: trimmed,
        description: book.description || '',
        genre: book.genre,
      });
      setBook(response.data);
      toast.success('Book renamed!');
    } catch (error) {
      toast.error('Failed to save title');
    }
  };

  const handleChapterUpdate = (updatedChapter) => {
    setChapters(chapters.map(c => 
      c.chapter_id === updatedChapter.chapter_id ? updatedChapter : c
    ));
    setEditingChapter(null);
  };

  // Handle drag end for chapter reordering
  const handleDragEnd = async (event) => {
    const { active, over } = event;
    
    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = chapters.findIndex(c => c.chapter_id === active.id);
    const newIndex = chapters.findIndex(c => c.chapter_id === over.id);
    
    // Optimistically update UI
    const reorderedChapters = arrayMove(chapters, oldIndex, newIndex).map((chapter, index) => ({
      ...chapter,
      chapter_number: index + 1
    }));
    setChapters(reorderedChapters);

    // Save to backend
    try {
      await axios.put(
        `${API}/books/${bookId}/reorder-chapters`,
        { 
          chapter_order: reorderedChapters.map(c => c.chapter_id)
        },
        
      );
      toast.success('Chapters reordered!');
    } catch (error) {
      console.error('Failed to reorder chapters:', error);
      toast.error('Failed to save chapter order');
      // Revert on error
      loadBookData();
    }
  };

  const getGenreColor = (genre) => {
    const colors = {
      memoir: 'bg-amber-50 text-amber-700',
      fiction: 'bg-purple-50 text-purple-700',
      business: 'bg-blue-50 text-blue-700',
      childrens: 'bg-rose-50 text-rose-700',
      freestyle: 'bg-violet-50 text-violet-700'
    };
    return colors[genre] || colors.freestyle;
  };

  if (loading) {
    return (
      <div className="story-page flex items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-[var(--story-primary)]/20 border-t-[var(--story-primary)]"></div>
      </div>
    );
  }

  if (!book) {
    return (
      <div className="story-page flex items-center justify-center">
        <div className="text-center">
          <h2 className="story-title mb-4 text-2xl">Book not found</h2>
          <Button onClick={() => navigate('/dashboard')} className="story-button-primary">Back to Dashboard</Button>
        </div>
      </div>
    );
  }

  const sortedChapters = [...chapters].sort((a, b) => a.chapter_number - b.chapter_number);
  const continueChapter = sortedChapters.find((chapter) => !chapter.content?.trim()) || sortedChapters[sortedChapters.length - 1];
  const interviewSections = interviewProgress?.sections?.length
    ? interviewProgress.sections
    : getInterviewSections(book.genre);
  const progressPercent = interviewProgress?.has_session
    ? Math.round((interviewProgress.sections_completed / Math.max(interviewProgress.total_sections || interviewSections.length, 1)) * 100)
    : 0;

  const contextualItems = [
    { icon: ArrowLeft, label: 'Back to Dashboard', onClick: () => navigate('/dashboard'), testId: 'back-to-dashboard-button' },
    { divider: true },
    { icon: Pencil, label: 'Edit Book', onClick: () => setShowEditBookModal(true), testId: 'edit-book-button' },
    { icon: Users, label: 'Collaborators', onClick: () => setShowCollaboratorsModal(true), testId: 'collaborators-button' },
    ...(chapters.length > 0 ? [{ icon: Download, label: 'Export Book', onClick: () => setShowExportModal(true), testId: 'export-book-button' }] : []),
    ...(!book.is_interview_mode ? [{ icon: Sparkles, label: 'Chapter Plan', onClick: () => setShowChapterPlan(prev => !prev), variant: showChapterPlan ? 'active' : 'default' }] : []),
  ];

  return (
    <AppShell contextualItems={contextualItems} backTo={{ label: 'Books', onClick: () => navigate('/dashboard') }} className="mobile-page">
      <main className="story-container pt-14 pb-5 sm:pt-12 sm:pb-10">
        <StorySurface className="mb-5 overflow-visible p-4 sm:p-5">
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <span className={`story-pill ${getGenreColor(book.genre)}`}>
                    {book.genre.charAt(0).toUpperCase() + book.genre.slice(1)}
                  </span>
                  <span className="story-meta">
                    {chapters.length} {chapters.length === 1 ? 'chapter' : 'chapters'}
                  </span>
                </div>
              <div className="flex items-center space-x-2 sm:space-x-3 mb-2 sm:mb-3">
                {editingTitle ? (
                  <input
                    autoFocus
                    value={titleDraft}
                    onChange={(e) => setTitleDraft(e.target.value)}
                    onBlur={handleTitleSave}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleTitleSave(); if (e.key === 'Escape') setEditingTitle(false); }}
                    className="text-2xl sm:text-4xl font-serif font-bold bg-transparent border-b-2 border-[#C4A882] outline-none text-[#2A2420] dark:text-[#F0EBE4] w-full max-w-lg"
                  />
                ) : (
                  <div className="flex items-center gap-2 group/title">
                    <h2
                      onClick={() => { setTitleDraft(book.title === 'Untitled Book' ? '' : book.title); setEditingTitle(true); }}
                      className={`story-title text-2xl sm:text-3xl cursor-text ${book.title === 'Untitled Book' ? 'text-[#C4A882] dark:text-[#C4A882]' : ''}`}
                    >
                      {book.title}
                    </h2>
                    {book.title === 'Untitled Book' && (
                      <span className="text-xs text-[#C4A882] font-normal opacity-0 group-hover/title:opacity-100 transition-opacity whitespace-nowrap">
                        Click to name
                      </span>
                    )}
                  </div>
                )}
              </div>
              {book.description && (
                <p className="story-body text-sm sm:text-base max-w-3xl">{book.description}</p>
              )}
            </div>
            
            <div className="flex flex-col sm:flex-row lg:justify-end gap-2 sm:min-w-[280px]">
              {book.is_interview_mode ? (
                <Button
                  onClick={() => setShowInterviewMode(true)}
                  data-testid="continue-interview-button"
                  className="story-button-primary px-5"
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  {interviewProgress?.has_session ? 'Continue Interview' : 'Start Interview'}
                </Button>
              ) : continueChapter ? (
                <Button
                  onClick={() => navigate(`/chapters/${continueChapter.chapter_id}`)}
                  className="story-button-primary px-5"
                >
                  <PenLine className="w-4 h-4 mr-2" />
                  Continue Writing
                </Button>
              ) : (
                <Button
                  onClick={() => setShowCreateModal(true)}
                  data-testid="add-first-chapter-button"
                  className="story-button-primary px-5"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add First Chapter
                </Button>
              )}
            </div>
          </div>
        </StorySurface>

        {/* Interview Book Progress Section */}
        {book.is_interview_mode && (
          <StorySurface subtle className="mb-5 p-4 sm:p-5" data-testid="interview-progress-section">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="w-9 h-9 bg-[var(--story-primary)] rounded-xl flex items-center justify-center">
                  <Mic className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-[var(--story-ink)]">Interview Mode</h3>
                  <p className="story-meta">Share your story through guided conversation</p>
                </div>
              </div>
              {interviewProgress?.has_session && (
                <div className="text-right">
                  <span className="text-xl font-bold text-[var(--story-primary)]">
                    {progressPercent}%
                  </span>
                  <p className="story-meta text-xs">complete</p>
                </div>
              )}
            </div>

            {/* Progress Bar */}
            {interviewProgress?.has_session && (
              <div className="mb-4">
                <StoryProgress value={progressPercent} />

                {/* Section arc list */}
                <div className="mt-3 grid grid-cols-1 gap-1">
                  {interviewSections.map(({ key, label }) => {
                    const compiled = (interviewProgress.compiled_chapters || []).some(c => c.section === key);
                    const isCurrent = interviewProgress.current_section === key;
                    const hasAnswers = (interviewProgress.sections_with_answers || []).includes(key);
                    return (
                      <div key={key} className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full shrink-0 ${
                          compiled  ? 'bg-[#5C4D6B] dark:bg-[#9A89AF]' :
                          isCurrent ? 'bg-[#C4A882] dark:bg-[#B8956A]' :
                          hasAnswers ? 'bg-[#D0C8BC] dark:bg-[#5A5248]' :
                          'bg-[#E8DED0] dark:bg-[#302820]'
                        }`} />
                        <span className={`text-xs ${
                          isCurrent
                            ? 'text-[#4A3D58] dark:text-[#C8B8D8] font-medium'
                            : compiled
                            ? 'text-[#5C4D6B] dark:text-[#9A89AF]'
                            : 'text-[#A09890] dark:text-[#6D655E]'
                        }`}>
                          {label}{compiled ? ' ✓' : isCurrent ? ' — in progress' : ''}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {!interviewProgress?.has_session && (
              <p className="story-meta">
                Answer guided questions and we'll help shape each section into chapters
              </p>
            )}
          </StorySurface>
        )}

        {/* Chapter Plan — inline section (non-interview books only) */}
        {!book.is_interview_mode && (
          <div className="mb-8">
            {chapters.length === 0 ? (
              <div className="space-y-3">
                <h3 className="story-section-title text-base">How would you like to start?</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <button
                    onClick={() => setShowCreateModal(true)}
                    data-testid="add-first-chapter-button"
                    className="story-surface-subtle group p-4 text-left transition-colors hover:border-[var(--story-line-strong)]"
                  >
                    <div className="w-9 h-9 bg-[#5C4D6B]/10 dark:bg-[#B0A0C0]/10 rounded-lg flex items-center justify-center mb-3">
                      <PenLine className="w-4 h-4 text-[#5C4D6B] dark:text-[#B0A0C0]" />
                    </div>
                    <h4 className="font-semibold text-[#2A2420] dark:text-[#F0EBE4] mb-1">Write Freely</h4>
                    <p className="text-sm text-[#7C766E] dark:text-[#8C847A]">Start with a blank page.</p>
                  </button>
                  <button
                    onClick={() => setShowChapterPlan(true)}
                    data-testid="generate-skeleton-button"
                    className="story-surface-subtle group p-4 text-left transition-colors hover:border-[var(--story-line-strong)]"
                  >
                    <div className="w-9 h-9 bg-[#5C4D6B]/10 dark:bg-[#B0A0C0]/10 rounded-lg flex items-center justify-center mb-3">
                      <Sparkles className="w-4 h-4 text-[#5C4D6B] dark:text-[#B0A0C0]" />
                    </div>
                    <h4 className="font-semibold text-[#2A2420] dark:text-[#F0EBE4] mb-1">Chapter Plan</h4>
                    <p className="text-sm text-[#7C766E] dark:text-[#8C847A]">Generate an outline with AI guidance.</p>
                  </button>
                </div>
                {showChapterPlan && (
                  <SkeletonCustomizerModal
                    book={book}
                    inline={true}
                    onClose={() => setShowChapterPlan(false)}
                    onGenerate={loadBookData}
                  />
                )}
              </div>
            ) : (
              <div className="mb-2">
                <button
                  onClick={() => setShowChapterPlan(!showChapterPlan)}
                  className="flex items-center gap-2 text-sm text-[#7C766E] dark:text-[#8C847A] hover:text-[#5C4D6B] dark:hover:text-[#B0A0C0] transition-colors"
                >
                  {showChapterPlan ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  <Sparkles className="w-3.5 h-3.5" />
                  <span>Chapter Plan</span>
                </button>
                {showChapterPlan && (
                  <div className="mt-3">
                    <SkeletonCustomizerModal
                      book={book}
                      inline={true}
                      onClose={() => setShowChapterPlan(false)}
                      onGenerate={loadBookData}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Chapters Section */}
        <div className="mb-8">
          <StorySectionHeader
            title="Chapters"
            count={chapters.length > 0 ? chapters.length : undefined}
            description="A quiet outline of your book. Drag on desktop to reorder."
            actions={chapters.length > 0 && (
              <Button
                onClick={() => setShowCreateModal(true)}
                size="sm"
                data-testid="add-chapter-button"
                className="story-button-primary px-4"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add
              </Button>
            )}
          />

          {chapters.length === 0 ? (
            <StoryEmptyState icon={FileText} title="Your chapters will appear here" />
          ) : (
            <>
              <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={chapters.map(c => c.chapter_id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-3 pl-0 sm:pl-10">
                  {sortedChapters.map((chapter) => (
                    <SortableChapterCard
                      key={chapter.chapter_id}
                      chapter={chapter}
                      onDelete={handleDeleteChapter}
                      onEdit={(ch) => setEditingChapter(ch)}
                      onClick={() => navigate(`/chapters/${chapter.chapter_id}`)}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
            </>
          )}
        </div>

        {/* Your Exports Section — collapsible */}
        {exports.length > 0 && (
          <div className="mt-10 border-t border-[#E0D8CC] dark:border-[#2A2520]/50 pt-6">
            <div className="flex items-center justify-between mb-3">
              <button
                onClick={() => {}}
                className="flex items-center gap-2 text-sm text-[#7C766E] dark:text-[#8C847A] font-medium"
              >
                <Download className="w-4 h-4" />
                Downloads ({exports.length})
              </button>
              <Button
                onClick={() => setShowExportModal(true)}
                size="sm"
                variant="outline"
                className="text-xs text-[#5C4D6B] dark:text-[#B0A0C0] border-[#C4B8D0] dark:border-[#3A3040] hover:bg-[#E8E0D4]/50 dark:hover:bg-[#2A2520]/50"
              >
                <Plus className="w-3.5 h-3.5 mr-1" />
                New Export
              </Button>
            </div>
            
            <div className="space-y-2">
              {exports.map((exportItem) => (
                <div 
                  key={exportItem.export_id}
                  className="flex items-center justify-between p-3 bg-white dark:bg-[#22201C] rounded-lg border border-[#E0D8CC] dark:border-[#2A2520]/50 hover:border-[#C4B0D0]/60 transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      exportItem.format === 'pdf' ? 'bg-red-100 dark:bg-red-900/20' :
                      exportItem.format === 'word' ? 'bg-blue-100 dark:bg-blue-900/20' :
                      'bg-green-100 dark:bg-green-900/20'
                    }`}>
                      <File className={`w-5 h-5 ${
                        exportItem.format === 'pdf' ? 'text-red-600 dark:text-red-400' :
                        exportItem.format === 'word' ? 'text-blue-600 dark:text-blue-400' :
                        'text-green-600 dark:text-green-400'
                      }`} />
                    </div>
                    <div>
                      <p className="font-medium text-sm text-[#2A2420] dark:text-[#F0EBE4]">
                        {exportItem.file_name}
                      </p>
                      <div className="flex items-center space-x-2 text-xs text-gray-500 dark:text-gray-400">
                        <span className="uppercase">{exportItem.format}</span>
                        <span>•</span>
                        <span>{formatFileSize(exportItem.file_size)}</span>
                        <span>•</span>
                        <span className="flex items-center">
                          <Clock className="w-3 h-3 mr-1" />
                          {formatDate(exportItem.created_at)}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Button
                      onClick={() => handleDownloadExport(exportItem)}
                      size="sm"
                      className="bg-[#5C4D6B] hover:bg-[#4A3D58] dark:bg-[#7B6A90] dark:hover:bg-[#6B5A80] text-white"
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                    <Button
                      onClick={() => handleDeleteExport(exportItem.export_id)}
                      size="sm"
                      variant="ghost"
                      className="text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>

      {showCreateModal && (
        <CreateChapterModal
          onClose={() => setShowCreateModal(false)}
          onCreate={handleCreateChapter}
          nextChapterNumber={chapters.length + 1}
        />
      )}

      {showExportModal && (
        <ExportBookModal
          book={book}
          chapters={chapters}
          onClose={() => {
            setShowExportModal(false);
            loadBookData(); // Refresh exports after closing
          }}
        />
      )}

      {showCollaboratorsModal && (
        <CollaboratorsModal
          book={book}
          onClose={() => setShowCollaboratorsModal(false)}
        />
      )}

      {showEditBookModal && (
        <EditBookModal
          book={book}
          onClose={() => setShowEditBookModal(false)}
          onUpdate={handleBookUpdate}
        />
      )}

      {editingChapter && (
        <EditChapterModal
          chapter={editingChapter}
          onClose={() => setEditingChapter(null)}
          onUpdate={handleChapterUpdate}
        />
      )}

      {/* Interview Mode - Full screen experience */}
      {showInterviewMode && (
        <InterviewMode
          bookId={bookId}
          bookTitle={book.title}
          genre={book.genre}
          onClose={() => setShowInterviewMode(false)}
          onChapterCreated={loadBookData}
        />
      )}
    </AppShell>
  );
}

export default BookDetailPage;
